import { Portal } from '@gorhom/portal';
import React, { ReactNode, useEffect, useRef, useState } from 'react';
import {
  Animated,
  Easing,
  StyleProp,
  StyleSheet,
  TouchableWithoutFeedback,
  ViewStyle,
} from 'react-native';

import generateID from '../../utils/generate-id';

import Backdrop from './Backdrop';

type Props = {
  /**
   * How long the animation should run
   */
  animationDuration?: number;
  /**
   * Position of the drawer. Only 'left' and 'bottom' are supported.
   */
  anchor?: 'left' | 'bottom';
  children?: ReactNode;
  /**
   * Invoked when the backdrop is pressed
   */
  onBackdropPress?: () => void;
  /**
   * Invoked when the Drawer is closed
   */
  onClose?: () => void;
  /**
   * Invoked when the Drawer is opened
   */
  onOpen?: () => void;
  /**
   * Display the Drawer or hide it
   */
  open?: boolean;
  /**
   * Customize the Drawer container's appearance. Useful for some responsive layout.
   */
  style?: StyleProp<ViewStyle>;
  /**
   * Accessibility label for the Drawer component
   */
  accessibilityLabel?: string;
  /**
   * Accessibility label for the Backdrop component
   */
  backdropAccessibilityLabel?: string;
  /**
   * Generates HTML and Test IDs
   */
  id?: string;
};

const DEFAULT_ANIM_DURATION = 250;
const EASING = Easing.bezier(0.4, 0, 0.2, 1);

const Drawer = (props: Props) => {
  const [isInitialized, setIsInitialized] = useState(false);
  const [isMounted, setIsMounted] = useState(false);
  const [drawerWidth, setDrawerWidth] = useState(0);
  const [drawerHeight, setDrawerHeight] = useState(0);

  const drawerPosition = useRef(new Animated.ValueXY({ x: 0, y: 0 })).current;

  useEffect(() => {
    // Don't run the animation when the component has just mounted
    if (!isInitialized) {
      setIsInitialized(true);

      return;
    }

    if (props.open) {
      setIsMounted(true);
    } else {
      Animated.timing(drawerPosition, {
        toValue: {
          x: props.anchor !== 'bottom' ? drawerWidth * -1 : 0,
          y: props.anchor === 'bottom' ? drawerHeight : 0,
        },
        duration: props.animationDuration || DEFAULT_ANIM_DURATION,
        easing: EASING,
        useNativeDriver: true,
      }).start(() => {
        props.onClose?.();
        setIsMounted(false);
      });
    }
  }, [props.open]);

  if (!isMounted) {
    return null;
  }

  return (
    <Portal>
      <Backdrop
        accessibilityLabel={props.backdropAccessibilityLabel}
        open={props.open}
        onPress={props.onBackdropPress}
        animationDuration={props.animationDuration}
        style={{
          flexDirection: props.anchor === 'bottom' ? 'column-reverse' : 'row',
        }}
        id={props.id && `${props.id}-backdrop`}
      >
        <TouchableWithoutFeedback onPress={(e) => e?.stopPropagation()}>
          <Animated.View
            style={[
              style.root,
              props.style,
              {
                transform: drawerPosition.getTranslateTransform() as any,
              },
            ]}
            onLayout={(e) => {
              const { width, height } = e.nativeEvent.layout;

              // Cache the drawer's dimensions
              setDrawerWidth(width);
              setDrawerHeight(height);

              if (props.anchor === 'bottom') {
                drawerPosition.y.setValue(height);
              } else {
                drawerPosition.x.setValue(width * -1);
              }

              Animated.timing(drawerPosition, {
                toValue: {
                  x: 0,
                  y: 0,
                },
                duration: props.animationDuration || DEFAULT_ANIM_DURATION,
                easing: EASING,
                useNativeDriver: true,
              }).start(() => {
                props.open && props.onOpen?.();
              });
            }}
            accessibilityLabel={props.accessibilityLabel}
            /** Generate HTML and Test IDs  */
            {...generateID(props.id)}
          >
            {props.children}
          </Animated.View>
        </TouchableWithoutFeedback>
      </Backdrop>
    </Portal>
  );
};

const style = StyleSheet.create({
  root: {
    backgroundColor: 'white',
    padding: 16,
  },
});

export default Drawer;
